import type { AppData } from './types';

export interface HealthScoreBreakdown {
  total: number;
  savingsRate: { score: number; max: 25; value: number };
  budgetAdherence: { score: number; max: 25; value: number };
  billPayment: { score: number; max: 25; value: number };
  savingsProgress: { score: number; max: 25; value: number };
  label: string;
  color: string;
}

export function calculateHealthScore(data: AppData): HealthScoreBreakdown {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // 1. Savings rate (income - expenses / income) → 0-25
  const monthTxns = data.transactions.filter(t => t.date.startsWith(currentMonth));
  const income = monthTxns.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const expenses = monthTxns.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const savingsRateValue = income > 0 ? Math.max(0, (income - expenses) / income) : 0;
  // 20%+ savings rate = full marks
  const savingsRateScore = Math.round(Math.min(savingsRateValue / 0.2, 1) * 25);

  // 2. Budget adherence → 0-25
  const monthBudgets = data.budgets.filter(b => b.month === currentMonth);
  let budgetAdherenceValue = 1;
  if (monthBudgets.length > 0) {
    const underBudget = monthBudgets.filter(b => {
      const spent = monthTxns
        .filter(t => t.type === 'expense' && t.category === b.category)
        .reduce((s, t) => s + t.amount, 0);
      return spent <= b.monthlyLimit;
    }).length;
    budgetAdherenceValue = underBudget / monthBudgets.length;
  }
  const budgetScore = Math.round(budgetAdherenceValue * 25);

  // 3. Bill payment (% of bills marked paid) → 0-25
  const totalBills = data.bills.length;
  const paidBills = data.bills.filter(b => b.paid).length;
  const billValue = totalBills > 0 ? paidBills / totalBills : 1;
  const billScore = Math.round(billValue * 25);

  // 4. Savings goals progress → 0-25
  const goals = data.savingsGoals ?? [];
  let savingsProgressValue = 0;
  if (goals.length > 0) {
    const avgProgress = goals.reduce((s, g) =>
      s + Math.min(g.currentAmount / Math.max(g.targetAmount, 1), 1), 0) / goals.length;
    savingsProgressValue = avgProgress;
  }
  const savingsScore = Math.round(savingsProgressValue * 25);

  const total = savingsRateScore + budgetScore + billScore + savingsScore;

  let label: string;
  let color: string;
  if (total >= 80) { label = 'Excellent'; color = 'text-success'; }
  else if (total >= 60) { label = 'Good'; color = 'text-primary'; }
  else if (total >= 40) { label = 'Fair'; color = 'text-warning'; }
  else { label = 'Needs Work'; color = 'text-destructive'; }

  return {
    total,
    savingsRate: { score: savingsRateScore, max: 25, value: Math.round(savingsRateValue * 100) },
    budgetAdherence: { score: budgetScore, max: 25, value: Math.round(budgetAdherenceValue * 100) },
    billPayment: { score: billScore, max: 25, value: Math.round(billValue * 100) },
    savingsProgress: { score: savingsScore, max: 25, value: Math.round(savingsProgressValue * 100) },
    label,
    color,
  };
}
